import { useState, useCallback } from 'react';
import { Order } from '../services/orders.service';
import ordersService from '../services/orders.service';
import { EnrichedDriver } from '../components/DriverDrawer';
import api from '../services/api';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Return value from useDriverDrawer hook
 */
/**
 * Suggested driver from smart dispatch scoring
 */
export interface SuggestedDriver {
  driver: string;
  rank: number;
  total_score: number;
  score_distance: number;
  score_workload: number;
  score_profitability: number;
  score_qualification: number;
  distance_miles: number;
  remaining_dispatches: number;
  current_pay: number;
  daily_goal: number;
  is_qualified: boolean;
}

export interface UseDriverDrawerReturn {
  /** Whether the drawer is open */
  isOpen: boolean;

  /** The order being processed */
  order: Order | null;

  /** List of enriched drivers */
  drivers: EnrichedDriver[];

  /** Top suggested drivers from smart dispatch scoring */
  suggestedDrivers: SuggestedDriver[];

  /** Loading state for driver fetch */
  loading: boolean;

  /** Error message if fetch failed */
  error: string | null;

  /** Opens the drawer and fetches drivers for an order */
  openDrawer: (order: Order, legnum?: number) => Promise<void>;

  /** Closes the drawer and resets state */
  closeDrawer: () => void;

  /** Handles driver selection */
  selectDriver: (driver: EnrichedDriver, onSelect: (driver: EnrichedDriver) => void) => void;
}

/**
 * Distance calculation using Haversine formula
 * @param lat1 Latitude of point 1
 * @param lon1 Longitude of point 1
 * @param lat2 Latitude of point 2
 * @param lon2 Longitude of point 2
 * @returns Distance in miles
 */
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 3959; // Radius of the Earth in miles
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance;
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * useDriverDrawer - Custom hook for managing driver drawer state and logic
 *
 * Handles:
 * - Opening/closing the drawer
 * - Fetching qualified drivers from multiple APIs
 * - Enriching driver data with location, distance, availability
 * - Sorting drivers by qualification, availability, and distance
 * - Managing loading and error states
 *
 * Usage:
 * ```tsx
 * const {
 *   isOpen,
 *   order,
 *   drivers,
 *   loading,
 *   error,
 *   openDrawer,
 *   closeDrawer,
 *   selectDriver
 * } = useDriverDrawer();
 *
 * // Open drawer for an order
 * <button onClick={() => openDrawer(order, legnum)}>Find Driver</button>
 *
 * // Render drawer
 * <DriverDrawer
 *   open={isOpen}
 *   onClose={closeDrawer}
 *   order={order}
 *   drivers={drivers}
 *   loading={loading}
 *   error={error}
 *   onSelectDriver={(driver) => selectDriver(driver, handleAssignment)}
 * />
 * ```
 */
export const useDriverDrawer = (): UseDriverDrawerReturn => {
  // ============================================================================
  // STATE
  // ============================================================================

  const [isOpen, setIsOpen] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [drivers, setDrivers] = useState<EnrichedDriver[]>([]);
  const [suggestedDrivers, setSuggestedDrivers] = useState<SuggestedDriver[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ============================================================================
  // FUNCTIONS
  // ============================================================================

  /**
   * Opens the drawer and fetches qualified drivers for an order
   *
   * Process:
   * 1. Fetches all qualified drivers for the order (checks requirements)
   * 2. Fetches real-time vehicle locations from Samsara (with VINs)
   * 3. Fetches current dispatch status for each driver
   * 4. Calculates distance from order origin to each driver
   * 5. Enriches driver data with location, distance, and availability
   * 6. Sorts drivers by: qualification → availability → distance
   */
  const openDrawer = useCallback(async (orderToFind: Order, legnum?: number) => {
    // Set order with legnum
    const orderWithLegnum = { ...orderToFind, legnum: legnum || orderToFind.legnum || 0 };
    setOrder(orderWithLegnum);
    setIsOpen(true);
    setLoading(true);
    setError(null);

    try {
      // Fetch data in parallel for efficiency
      const [validationResponse, locationsWithVinsResponse, dispatchResponse, suggestResponse] = await Promise.all([
        // 1. Find all qualified drivers for this order
        ordersService.findValidDrivers(orderWithLegnum.pronumber, legnum),

        // 2. Get real-time vehicle locations with VINs (efficient joined query)
        api.get('/samsara/vehicles/locations-with-vins').then(r => r.data),

        // 3. Get current dispatch status for all drivers
        ordersService.getLatestDispatches(undefined, undefined),

        // 4. Get smart dispatch suggestions - try pre-computed alternatives first, fall back to on-demand
        api.get(`/smart-dispatch/suggestions/alternatives?pronumber=${orderWithLegnum.pronumber}&legnum=${legnum || 0}`)
          .then(r => r.data && r.data.count > 0 ? r.data : null)
          .catch(() => null)
          .then(precomputed => precomputed ||
            api.post(`/smart-dispatch/suggestions/suggest-alternatives?pronumber=${orderWithLegnum.pronumber}&legnum=${legnum || 0}`)
              .then(r => r.data)
              .catch(() => null)
          )
      ]);

      const qualifiedDrivers = validationResponse.drivers;
      const locationsWithVins = locationsWithVinsResponse.data || [];
      const dispatches = dispatchResponse.drivers || [];

      // Create VIN to location map for fast lookup
      const vinToLocationMap = new Map();
      const vinToDispatcherMap = new Map();
      locationsWithVins.forEach((item: any) => {
        if (item.vin && item.location) {
          vinToLocationMap.set(item.vin, item.location);
        }
        if (item.vin && item.dispatchInfo?.dispatcher) {
          vinToDispatcherMap.set(item.vin, item.dispatchInfo.dispatcher);
        }
      });

      // Parse origin coordinates from order
      let originLat: number | null = null;
      let originLng: number | null = null;

      if (orderWithLegnum.originCoordinates && orderWithLegnum.originCoordinates.includes(',')) {
        // Handle multiple coordinate pairs separated by semicolons (take first pair)
        const firstCoordPair = orderWithLegnum.originCoordinates.split(';')[0].trim();
        const coords = firstCoordPair.split(',').map(c => parseFloat(c.trim()));

        if (coords.length >= 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
          originLat = coords[0];
          originLng = coords[1];
        } else {
          console.warn('Failed to parse coordinates from:', orderWithLegnum.originCoordinates);
        }
      } else {
        console.warn('No originCoordinates or missing comma:', orderWithLegnum.originCoordinates);
      }

      // Enrich drivers with location, distance, and dispatch status
      const enrichedDrivers: EnrichedDriver[] = qualifiedDrivers.map((driver: any) => {
        // Match driver to vehicle by VIN
        const driverVin = driver.truckVin;
        const location = driverVin ? vinToLocationMap.get(driverVin) : null;
        const dispatcher = driverVin ? vinToDispatcherMap.get(driverVin) : null;

        // Calculate distance from origin if we have both locations
        let distance = null;
        if (location && originLat !== null && originLng !== null) {
          distance = calculateDistance(
            originLat,
            originLng,
            location.latitude,
            location.longitude
          );
        }

        // Find matching dispatch status by unit number
        const dispatchInfo = dispatches.find((d: any) =>
          d.unitNumber === driver.unitNumber
        );

        const enrichedDriver = {
          ...driver,
          dispatcher: dispatcher,
          location: location ? {
            latitude: location.latitude,
            longitude: location.longitude,
            speed: location.speed,
            hosStatus: null, // Samsara vehicle locations don't include HOS status
            time: location.time
          } : null,
          distanceFromOrigin: distance,
          dispatchInfo: dispatchInfo?.dispatch ? {
            proNumber: dispatchInfo.dispatch.proNumber,
            isCompleted: dispatchInfo.dispatch.isCompleted,
            originName: dispatchInfo.dispatch.origin?.name || 'Unknown',
            destinationName: dispatchInfo.dispatch.destination?.name || 'Unknown'
          } : null
        };

        return enrichedDriver;
      });

      // Sort drivers by priority:
      // 1. Qualified drivers first
      // 2. Within qualified: Available (completed dispatch) first
      // 3. Within same qualification/availability: Closest drivers first
      enrichedDrivers.sort((a, b) => {
        // Qualified drivers come first
        if (a.isQualified !== b.isQualified) {
          return b.isQualified ? 1 : -1;
        }

        // Within qualified drivers, prioritize available over busy
        const aAvailable = a.dispatchInfo?.isCompleted !== false; // Available if no dispatch or completed
        const bAvailable = b.dispatchInfo?.isCompleted !== false;
        if (aAvailable !== bAvailable) {
          return bAvailable ? 1 : -1; // Available drivers first
        }

        // Within same qualification and availability, sort by distance
        if (a.distanceFromOrigin === null && b.distanceFromOrigin === null) return 0;
        if (a.distanceFromOrigin === null) return 1;
        if (b.distanceFromOrigin === null) return -1;
        return a.distanceFromOrigin - b.distanceFromOrigin;
      });

      setDrivers(enrichedDrivers);
      setSuggestedDrivers(suggestResponse?.alternatives || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch drivers:', err);
      setError('Error loading drivers. Please try again.');
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Closes the drawer and resets all state
   */
  const closeDrawer = useCallback(() => {
    setIsOpen(false);
    setOrder(null);
    setDrivers([]);
    setSuggestedDrivers([]);
    setError(null);
    setLoading(false);
  }, []);

  /**
   * Handles driver selection
   * Calls the provided callback and keeps drawer open for now
   * (caller can choose to close drawer after successful assignment)
   */
  const selectDriver = useCallback((driver: EnrichedDriver, onSelect: (driver: EnrichedDriver) => void) => {
    onSelect(driver);
    // Note: Not closing drawer automatically - let parent decide
    // closeDrawer();
  }, []);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    isOpen,
    order,
    drivers,
    suggestedDrivers,
    loading,
    error,
    openDrawer,
    closeDrawer,
    selectDriver,
  };
};

export default useDriverDrawer;
